/**
 * tRPC client.
 *
 * Architecture §6.3: the SPA calls the API by absolute URL. CORS is locked
 * server-side; cookies travel with credentials.
 *
 * Two clients are exported:
 *   - `trpc` — the React-hooks client used by components.
 *   - `vanillaTrpc` — a hookless client used by router `beforeLoad` guards
 *     (which run outside React) and any other non-component callers.
 *
 * Both share the same `httpBatchLink` config so a request from `beforeLoad`
 * and a follow-on hook query batch into the same wire call when the timing
 * lines up.
 */
import { createTRPCReact } from "@trpc/react-query";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import type { AppRouter } from "@flint/api/router";
import { WEB_ENV } from "./env.ts";

export const trpc = createTRPCReact<AppRouter>();

function makeLinks() {
  return [
    httpBatchLink({
      url: `${WEB_ENV.VITE_FLINT_API_URL}/api/trpc`,
      fetch(url, options) {
        return fetch(url, { ...options, credentials: "include" } as RequestInit);
      },
    }),
  ];
}

export function makeTrpcClient() {
  return trpc.createClient({ links: makeLinks() });
}

/**
 * Hookless tRPC client. Use from `beforeLoad` route guards, plain async
 * helpers, anywhere outside the React tree. Tied to module scope so a single
 * batch link is reused across the app.
 */
export const vanillaTrpc = createTRPCClient<AppRouter>({ links: makeLinks() });
